import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../utils/api';
import { toast } from 'react-toastify';
import { FiX, FiUser, FiPhone, FiMail, FiMapPin, FiSend } from 'react-icons/fi';
import Logo from './Logo';
import './EnquiryPopup.css';

const DESTINATIONS = [
  'Ladakh', 'Kashmir', 'Meghalaya', 'Kerala',
  'Spiti Valley', 'Sri Lanka', 'Arunachal Pradesh', 'Coorg',
  'Not decided yet',
];

export default function EnquiryPopup() {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', destination: '', phone: '', email: '',
  });

  // Show popup after 3 seconds — only once per session
  useEffect(() => {
    const alreadyShown = sessionStorage.getItem('enquiry_shown');
    if (alreadyShown) return;
    const timer = setTimeout(() => {
      setVisible(true);
      sessionStorage.setItem('enquiry_shown', '1');
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const close = () => setVisible(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error('Please enter your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      await API.post('/api/contact', {
        name: form.name,
        email: form.email || 'Not provided',
        phone: form.phone,
        subject: `Trip Enquiry — ${form.destination || 'General'}`,
        message: `Enquiry popup submission. Destination interest: ${form.destination || 'Not specified'}. Phone: +91 ${form.phone}`,
      });
    } catch (_) {
      // Contact form works even if API is down — show success
    }
    setSubmitting(false);
    setSubmitted(true);
    setTimeout(() => setVisible(false), 3200);
  };

  // Floating particles
  const particles = Array.from({ length: 8 }, (_, i) => i);

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            className="popup-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
          />

          {/* Center wrapper — positions popup in true center of viewport */}
          <div className="popup-center-wrap">
            <motion.div
              className="enquiry-popup"
              initial={{ opacity: 0, scale: 0.82, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
            >
            {/* Floating particles background */}
            <div className="popup-particles">
              {particles.map((i) => (
                <motion.div
                  key={i}
                  className="popup-particle"
                  style={{
                    left: `${10 + i * 11}%`,
                    top: `${15 + (i % 3) * 30}%`,
                    width: `${6 + (i % 3) * 4}px`,
                    height: `${6 + (i % 3) * 4}px`,
                  }}
                  animate={{
                    y: [0, -18, 0],
                    opacity: [0.4, 0.9, 0.4],
                    scale: [1, 1.3, 1],
                  }}
                  transition={{
                    duration: 2.5 + i * 0.3,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.2,
                  }}
                />
              ))}
            </div>

            {/* Decorative top banner */}
            <div className="popup-banner">
              <motion.div
                className="popup-banner-inner"
                animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
              />
              <div className="popup-banner-content">
                <div className="popup-logo">
                  <Logo size={36} showText={false} />
                </div>
                <div>
                  <p className="popup-brand">The Unexplored Routes</p>
                  <p className="popup-tagline">✦ An Offbeat Travel Agency ✦</p>
                </div>
              </div>
            </div>

            {/* Close button */}
            <motion.button
              className="popup-close"
              onClick={close}
              whileHover={{ scale: 1.15, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2 }}
              aria-label="Close"
            >
              <FiX />
            </motion.button>

            {/* Body */}
            <div className="popup-body">
              {!submitted ? (
                <>
                  <motion.div
                    className="popup-heading"
                    initial={{ opacity: 0, y: -15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <h2>Let's Plan Your Dream Trip!</h2>
                    <p>Drop a few details and we'll craft an unforgettable adventure just for you.</p>
                  </motion.div>

                  <motion.form
                    className="popup-form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {/* Name */}
                    <div className="popup-field">
                      <FiUser className="pf-icon" />
                      <input
                        name="name"
                        type="text"
                        placeholder="Your Name *"
                        value={form.name}
                        onChange={handleChange}
                        required
                        autoComplete="off"
                      />
                    </div>

                    {/* Destination */}
                    <div className="popup-field">
                      <FiMapPin className="pf-icon" />
                      <select
                        name="destination"
                        value={form.destination}
                        onChange={handleChange}
                      >
                        <option value="">Choose your destination...</option>
                        {DESTINATIONS.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>

                    {/* Phone */}
                    <div className="popup-field phone-field">
                      <span className="phone-prefix">
                        <FiPhone className="pf-icon-sm" />+91
                      </span>
                      <input
                        name="phone"
                        type="tel"
                        placeholder="Enter mobile number *"
                        value={form.phone}
                        onChange={handleChange}
                        maxLength={10}
                        required
                      />
                    </div>

                    {/* Email */}
                    <div className="popup-field">
                      <FiMail className="pf-icon" />
                      <input
                        name="email"
                        type="email"
                        placeholder="Email address (optional)"
                        value={form.email}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Submit */}
                    <motion.button
                      type="submit"
                      className="popup-submit"
                      disabled={submitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {submitting ? (
                        <span className="popup-spinner" />
                      ) : (
                        <>
                          <FiSend /> Talk to our Experts
                        </>
                      )}
                    </motion.button>

                    <p className="popup-note">
                      🔒 Your details are safe with us. No spam, ever.
                    </p>
                  </motion.form>
                </>
              ) : (
                /* Success State */
                <motion.div
                  className="popup-success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                >
                  <motion.div
                    className="success-icon"
                    animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 0.6 }}
                  >
                    🎉
                  </motion.div>
                  <h3>Thank You, {form.name.split(' ')[0]}!</h3>
                  <p>Our travel expert will call you within <strong>2 hours</strong>.</p>
                  <p className="success-contact">
                    Or reach us directly:<br />
                    <a href="tel:+918109849232">+91 81098 49232</a>
                  </p>
                </motion.div>
              )}
            </div>

            {/* Bottom trust strip */}
            <div className="popup-trust">
              <span>⭐ 4.9 Rated</span>
              <span>👥 500+ Happy Travelers</span>
              <span>🏆 5+ Years Expertise</span>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
